import type { CmfRedecDebtPayload } from "./soyioApi";

type CmfSnapshot = {
  capturedAt: string;
  capturedWith: string;
  response: Record<string, unknown>;
};

type DebtRow = Array<string | number>;

const CAPTURED_AT = "2026-06-30T12:00:00-04:00";
const CAPTURED_WITH = "CMF sandbox /api/v1/consulta using local repo mTLS p12";

function period2025(index: number): string {
  return `2025${String(12 - index).padStart(2, "0")}`;
}

function highPeriod(index: number): string {
  const date = new Date(Date.UTC(2025, 11 - index, 1));
  return `${date.getUTCFullYear()}${String(date.getUTCMonth() + 1).padStart(
    2,
    "0"
  )}`;
}

function debtRow(
  period: string,
  rut: string,
  type: number,
  operations: number,
  amount: number,
  installment: number,
  term: number,
  direct: number,
  indirect: number,
  available: number,
  currentAmount: number,
  overdue: number[] = [],
  maxOverdue = 0
): DebtRow {
  const mora = Array.from({ length: 9 }, (_, index) => overdue[index] ?? 0);
  return [
    period,
    rut,
    1,
    type,
    1,
    operations,
    amount,
    installment,
    term,
    0,
    indirect,
    0,
    0,
    direct,
    currentAmount,
    ...mora,
    maxOverdue,
    available,
    0,
  ];
}

function mediumRows(): DebtRow[] {
  const rut = "55555555-5";
  const mortgageMora: Record<number, { overdue: number[]; max: number }> = {
    1: { overdue: [150000], max: 12 },
    2: { overdue: [200000], max: 18 },
    3: { overdue: [150000, 100000], max: 25 },
    4: { overdue: [150000, 100000], max: 22 },
  };
  const mortgageCurrent = [
    3200000, 3150000, 3200000, 3250000, 3350000, 3700000, 3800000, 3900000,
    4000000, 4000000, 4000000, 4000000,
  ];

  return Array.from({ length: 12 }).flatMap((_, index) => {
    const period = period2025(index);
    const mortgageAmount = Math.min(3200000 + index * 100000, 4000000);
    const consumerAmount = Math.min(1800000 + index * 100000, 2500000);
    const commercialAmount = 52000000 + index * 200000;
    const mora = mortgageMora[index];

    return [
      debtRow(
        period,
        rut,
        2,
        1,
        mortgageAmount,
        95000,
        18 + index,
        4000000,
        0,
        0,
        mortgageCurrent[index],
        mora?.overdue,
        mora?.max
      ),
      debtRow(
        period,
        rut,
        7,
        1,
        consumerAmount,
        90000,
        1,
        2500000,
        0,
        0,
        consumerAmount
      ),
      debtRow(
        period,
        rut,
        10,
        1,
        commercialAmount,
        420000,
        280 + index,
        75000000,
        0,
        0,
        commercialAmount
      ),
    ];
  });
}

function highRows(): DebtRow[] {
  const rut = "77777777-7";
  const mortgageMora: Record<number, { current: number; overdue: number[]; max: number }> = {
    1: { current: 15000000, overdue: [200000], max: 15 },
    2: { current: 15100000, overdue: [200000, 100000], max: 25 },
    3: { current: 15200000, overdue: [250000, 150000], max: 35 },
    4: { current: 15300000, overdue: [300000, 200000], max: 45 },
    5: { current: 15700000, overdue: [200000, 100000], max: 28 },
    6: { current: 16000000, overdue: [200000], max: 18 },
    7: { current: 16200000, overdue: [200000], max: 12 },
    8: { current: 16450000, overdue: [150000], max: 10 },
  };

  return Array.from({ length: 36 }).flatMap((_, index) => {
    const period = highPeriod(index);
    const mortgageAmount = Math.min(15000000 + index * 200000, 18000000);
    const personalAmount = Math.min(4200000 + index * 100000, 5000000);
    const consumerAmount = Math.min(2100000 + index * 100000, 3000000);
    const commercialAmount = 62000000 + index * 150000;
    const contingentAmount = Math.min(8500000 + index * 150000, 12000000);
    const mora = mortgageMora[index];

    return [
      debtRow(
        period,
        rut,
        1,
        1,
        mortgageAmount,
        250000,
        24 + index,
        18000000,
        5000000,
        0,
        mora?.current ?? mortgageAmount,
        mora?.overdue,
        mora?.max
      ),
      debtRow(
        period,
        rut,
        2,
        1,
        personalAmount,
        140000,
        18 + index,
        5000000,
        0,
        0,
        personalAmount
      ),
      debtRow(
        period,
        rut,
        7,
        1,
        consumerAmount,
        105000,
        1,
        3000000,
        0,
        0,
        consumerAmount
      ),
      debtRow(
        period,
        rut,
        10,
        1,
        commercialAmount,
        520000,
        300 + index,
        85000000,
        0,
        0,
        commercialAmount
      ),
      debtRow(
        period,
        rut,
        31,
        1,
        contingentAmount,
        180000,
        Math.min(36 + index, 60),
        12000000,
        12000000,
        0,
        contingentAmount
      ),
    ];
  });
}

const snapshots: Record<string, CmfSnapshot> = {
  "444444444": {
    capturedAt: CAPTURED_AT,
    capturedWith: CAPTURED_WITH,
    response: {
      status: true,
      message: "OK",
      code: 200,
      subcode: 200,
      type: "GET",
      version: "api-v1-2025",
      datetime: "2025-12-17T00:00:00+00:00",
      response: {
        resumen: {
          total_registros: 4,
          total_periodos: 2,
          periodo_inicio: "202511",
          periodo_fin: "202512",
          rut: "44444444-4",
          monto_total_deuda_vigente: 12500000,
          monto_total_mora_acumulada: 0,
          promedio_mora_mensual: 0,
          periodos_con_mora: 0,
          periodos_sin_mora: 2,
          mora_maxima_historica: 0,
          cantidad_operaciones_vigentes: 2,
        },
        deudas: [
          debtRow("202512", "44444444-4", 2, 1, 4500000, 150000, 24, 5000000, 0, 0, 4500000),
          debtRow("202512", "44444444-4", 10, 1, 45000000, 380000, 240, 60000000, 0, 0, 45000000),
          debtRow("202511", "44444444-4", 2, 1, 4600000, 150000, 25, 5000000, 0, 0, 4600000),
          debtRow("202511", "44444444-4", 10, 1, 45200000, 380000, 241, 60000000, 0, 0, 45200000),
        ],
      },
    },
  },
  "555555555": {
    capturedAt: CAPTURED_AT,
    capturedWith: CAPTURED_WITH,
    response: {
      status: true,
      message: "OK",
      code: 200,
      subcode: 200,
      type: "GET",
      version: "api-v1-2025",
      datetime: "2025-12-17T00:00:00+00:00",
      response: {
        resumen: {
          total_registros: 36,
          total_periodos: 12,
          periodo_inicio: "202501",
          periodo_fin: "202512",
          rut: "55555555-5",
          monto_total_deuda_vigente: 28500000,
          monto_total_mora_acumulada: 850000,
          promedio_mora_mensual: 70833,
          periodos_con_mora: 4,
          periodos_sin_mora: 8,
          mora_maxima_historica: 25,
          cantidad_operaciones_vigentes: 3,
        },
        deudas: mediumRows(),
      },
    },
  },
  "777777777": {
    capturedAt: CAPTURED_AT,
    capturedWith: CAPTURED_WITH,
    response: {
      status: true,
      message: "OK",
      code: 200,
      subcode: 200,
      type: "GET",
      version: "api-v1-2025",
      datetime: "2025-12-17T00:00:00+00:00",
      response: {
        resumen: {
          total_registros: 180,
          total_periodos: 36,
          periodo_inicio: "202301",
          periodo_fin: "202512",
          rut: "77777777-7",
          monto_total_deuda_vigente: 95800000,
          monto_total_mora_acumulada: 2150000,
          promedio_mora_mensual: 59722,
          periodos_con_mora: 8,
          periodos_sin_mora: 28,
          mora_maxima_historica: 45,
          cantidad_operaciones_vigentes: 5,
        },
        deudas: highRows(),
      },
    },
  },
  "999999999": {
    capturedAt: CAPTURED_AT,
    capturedWith: CAPTURED_WITH,
    response: {
      status: false,
      message: "NOT_FOUND",
      code: 404,
      subcode: 404,
      type: "GET",
      version: "api-v1-2025",
      datetime: "2025-12-17T00:00:00+00:00",
      response: {
        error: "NOT_FOUND",
        message: "No se encontraron registros para los parametros especificados",
      },
    },
  },
};

function normalizeRut(rut: string): string {
  return rut.replace(/[^0-9kK]/g, "").toUpperCase();
}

export function cmfRedecSnapshotForPayload(body: CmfRedecDebtPayload) {
  const snapshot = snapshots[normalizeRut(body.rut_consultado)];
  if (!snapshot) return null;

  return {
    ...snapshot,
    response: {
      ...snapshot.response,
      snapshot_metadata: {
        captured_at: snapshot.capturedAt,
        captured_with: snapshot.capturedWith,
        presented_at: new Date().toISOString(),
        runtime_source: "local_real_cmf_snapshot",
        request_dates: {
          fecha_inicio_otorgamiento: body.fecha_inicio_otorgamiento,
          hora_inicio_otorgamiento: body.hora_inicio_otorgamiento,
          fecha_fin_consentimiento: body.fecha_fin_consentimiento,
          hora_fin_consentimiento: body.hora_fin_consentimiento,
        },
      },
    },
  };
}
